/**
 * MemberProfileModal — Employee Profile modal, opened by clicking a Team
 * Roster row (gated on `storeSettings.teamMgmt.teamRoster.edit` by the
 * caller).
 *
 * There is no separate view claim and no independent print permission — the
 * Print button is reachable only because opening the modal already required
 * the roster-edit claim, so it is never gated on anything of its own
 * (868ka6fk0).
 *
 * The header status/role badges and the four sections below (Back Office
 * Integration, Employee Record, User Information, History) are GET field
 * mapping (868ka6fr1). The Back Office Integration card's three fields
 * (Importer Sync, Employee Status, Employee Role) are independently editable
 * via the shared FieldSelectDialog (868ka6g3q) — only one editable at a time
 * (bohEditLock), success/error surfaced via the in-modal actionBanner rather
 * than a toast (native toasts render behind this modal's stacking context).
 * Every other write action (Employee Record, Promote, Demote, drift Fixes,
 * Set Primary) is a separate subtask (5–10).
 */
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';
import { isNull, merge, omitBy } from 'lodash';
import * as Clipboard from 'expo-clipboard';
import { Box, HStack, Modal, Pressable, ScrollView, VStack, WarningIcon } from 'native-base';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CheckCircle,
  CircleArrowDownIcon,
  CircleArrowUpIcon,
  ClockIcon,
  ControlInput,
  CopyIcon,
  FormHelperText,
  InputField,
  PencilIcon,
  PrinterIcon,
  RefreshCwIcon,
  ShieldUserIcon,
  StarIcon,
  Text,
  UserRoundCheckIcon,
  UserRoundIcon,
  UserRoundXIcon,
} from '@pimm/base';
import {
  Badge,
  YesNoBadge,
  formatBirthday,
  formatDateShort,
  formatRelativeDays,
  hasRoleMismatch,
  hasStatusMismatch,
  importSyncIconColor,
  roleBadgeStyle,
  statusBadgeStyle,
  syncModeBadgeStyle,
  userStatusBadgeStyle,
} from './team-management-table-shared';
import { Card as SectionCard } from './section';
import { ReadOnlyField } from './read-only-field';
import { MemberProfilePrintView } from './team-management-member-profile-print-view';
import { FieldSelectDialog } from './team-management-field-select-dialog';
import { ConfirmModal, InfoCard } from './team-management-confirm-modal';
import { useStoreSettings } from '../context/store-settings.context';
import { useUpdateTeamMember } from '../hooks/update-team-member.hook';
import { useResyncUserRole } from '../hooks/resync-user-role.hook';
import { useResyncUserStatus } from '../hooks/resync-user-status.hook';
import { useSetPrimaryManager } from '../hooks/set-primary-manager.hook';
import { FormEmployeeRecordSchema, useFormEmployeeRecord } from '../hooks/form-employee-record.hook';
import { fromIsoDate, fromIsoMonthDay, getIsoYear, maskDate, maskMonthDay, toIsoBirthDate, toIsoDate } from '../utils';
import type { ColumnContext } from './team-management-columns';
import type { ImportSyncMode, TeamMember, TeamMemberRole, TeamMemberStatus } from '../types';
import { RECOVERY_EMAIL_STATUS_VERIFIED } from '../types';
import type { EmployeeStatusEnum, ImportSyncModeEnum } from '@pimm/services/lib/sms-workforce/types';

type Props = {
  member: TeamMember | null;
  onClose: () => void;
  onMemberUpdated: (next: TeamMember) => void;
  columnContext: ColumnContext;
  vendorName?: string;
  siteName: string;
  customerName: string;
  address: string;
  chainName: string;
  brandLogoUrl?: string;
  generatedByName: string;
  storeCode?: string;
  // Gates the drift-banner "Fix" buttons — same claim that already gates
  // row-click and Employee Record editing (storeSettings.teamMgmt.teamRoster.edit).
  canEditRoster?: boolean;
  // Gates the "Set as Primary Manager" action — its own dedicated claim
  // (storeSettings.teamMgmt.teamRoster.setPrimary), NOT teamRoster.edit.
  canSetPrimary?: boolean;
};

// Label + colored badge field — mirrors ReadOnlyField's label styling but
// swaps the value slot for a Badge/YesNoBadge. Used by the 6 status-like
// fields below (Importer Sync, Employee Status, Employee Role, PIN Set,
// User Status, User Role).
const BadgeField = ({ label, children }: { label: string; children: ReactNode }) => (
  <VStack space={0.5} flex={1} minW="170px">
    <Text size="md" fontWeight={500} color="gray.600">
      {label}
    </Text>
    <Box alignSelf="flex-start">{children}</Box>
  </VStack>
);

// Edit affordance for the 3 Back Office Integration fields — hidden while
// any of the three is already in edit mode (bohEditLock), matching the
// prototype's mutual-exclusion behavior.
const FieldEditPencil = ({ onPress }: { onPress: () => void }) => (
  <Pressable onPress={onPress} p={1} rounded="sm" _hover={{ bg: 'gray.100' }}>
    <PencilIcon size={3} color="gray.700" />
  </Pressable>
);

export const MemberProfileModal = ({
  member,
  onClose,
  onMemberUpdated,
  columnContext,
  vendorName,
  siteName,
  customerName,
  address,
  chainName,
  brandLogoUrl,
  generatedByName,
  storeCode,
  canEditRoster,
  canSetPrimary,
}: Props) => {
  const { t } = useTranslation();
  const [isPrintOpen, setIsPrintOpen] = useState(false);

  // Only one Back Office Integration field may be edited at a time — the
  // other two hide their pencils while this is set (mirrors the prototype's
  // bohEditLock, expressed as a single union instead of 3 OR'd booleans).
  const [editingField, setEditingField] = useState<'importerSync' | 'employeeStatus' | 'employeeRole' | null>(null);
  const bohEditLock = editingField !== null;

  const [actionBanner, setActionBanner] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  useEffect(() => {
    if (!actionBanner) return;
    const id = setTimeout(() => setActionBanner(null), 5000);
    return () => clearTimeout(id);
  }, [actionBanner]);

  const updateTeamMember = useUpdateTeamMember();
  const resyncUserRole = useResyncUserRole();
  const resyncUserStatus = useResyncUserStatus();
  const setPrimaryManager = useSetPrimaryManager();

  const site = useStoreSettings(_ => _.site);
  const onConfigChange = useStoreSettings(_ => _.onConfigChange);
  // Keeps General Info's "Primary Site Manager" card (site.primaryContact)
  // in sync — that card has no fetch of its own, it just renders whatever
  // is on the shared SiteConfigContext, which nothing else refreshes after
  // a Team Roster edit (see set-primary-manager.hook.ts).
  const syncPrimaryContact = (nextMember: TeamMember) => {
    onConfigChange({
      ...site,
      primaryContact: {
        firstName: nextMember.firstName,
        lastName: nextMember.lastName,
        email: nextMember.email,
        mobilePhone: nextMember.mobileNumber,
      },
    });
  };

  const isEditingEmployee = useBoolean();
  const employeeForm = useFormEmployeeRecord({
    defaultValues: {
      firstName: member?.firstName,
      lastName: member?.lastName,
      title: member?.title,
      birthDate: fromIsoMonthDay(member?.birthDate),
      hireDate: fromIsoDate(member?.hireDate),
      email: member?.email,
      mobileNumber: member?.mobileNumber,
    },
  });
  // Keep the form synced to fresh cache data while idle (mirrors contact-block.tsx).
  useEffect(() => {
    if (!member || isEditingEmployee.value) return;
    employeeForm.reset(
      merge(
        FormEmployeeRecordSchema.getDefault(),
        omitBy(
          {
            firstName: member.firstName,
            lastName: member.lastName,
            title: member.title,
            birthDate: fromIsoMonthDay(member.birthDate),
            hireDate: fromIsoDate(member.hireDate),
            email: member.email,
            mobileNumber: member.mobileNumber,
          },
          isNull,
        ),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  const scrollRef = useRef<any>(null);
  const handleOpenImporterSyncFromBanner = () => {
    setEditingField('importerSync');
    scrollRef.current?.scrollTo?.({ y: 0, animated: true });
  };

  const handleEmployeeRecordCancel = () => {
    employeeForm.reset();
    isEditingEmployee.setFalse();
  };

  const handleEmployeeRecordSave = employeeForm.handleSubmit(async values => {
    if (!member) return;
    try {
      const next = await updateTeamMember.mutateAsync({
        employeeId: member.id!,
        ...values,
        // Birthday is year-less in the UI (MM / DD only) — preserve whatever
        // real year is already on this record rather than overwriting it
        // with a placeholder, since unlike Add Employee there IS a prior
        // value here worth not destroying.
        birthDate: toIsoBirthDate(values.birthDate, getIsoYear(member.birthDate)) ?? '',
        hireDate: toIsoDate(values.hireDate) ?? '',
      });
      onMemberUpdated(next);
      if (member.user?.isPrimary) syncPrimaryContact(next);
      employeeForm.reset(values);
      isEditingEmployee.setFalse();
      setActionBanner({ tone: 'success', message: t('store_settings:toast_employee_saved') });
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:employee_record_save_error_banner') });
    }
  });

  const employeeCountDirty = Object.keys(employeeForm.formState.dirtyFields).length;
  const isEmployeeSaving = updateTeamMember.isLoading;
  // Matches contact-block.tsx's exact helper — ControlInput's renderErrorText
  // receives `key?: string`, so guard the undefined case before calling t().
  const renderErrorText = (key?: string) => (key ? t(key) : undefined);

  // Promote/Demote/Set Primary confirm-dialog state — declared unconditionally,
  // before the `!member` guard below, because Hooks must run the same number
  // of times on every render regardless of `member` (this component stays
  // mounted with member alternating between an object and null as the modal
  // opens/closes, so a Hook declared after the guard would make React throw
  // "Rendering more hooks than during the previous render" on the first open).
  const [isPromoteOpen, setIsPromoteOpen] = useState(false);
  const [promoteLoginInfo, setPromoteLoginInfo] = useState<{ userName: string; role: TeamMemberRole } | null>(null);
  const [loginInfoCopied, setLoginInfoCopied] = useState(false);
  const [isDemoteOpen, setIsDemoteOpen] = useState(false);
  const [isSetPrimaryOpen, setIsSetPrimaryOpen] = useState(false);

  if (!member) return null;

  // Employee Role gating matrix — while Importer Sync is on (Auto/Protected),
  // importers only promote, never demote, so role edits are promote-only;
  // once Locked, the importer isn't touching the record so lateral changes
  // (Manager <-> User) are fine both ways. An SMS Employee with no linked
  // user is handled entirely by the (separate, subtask-6) Promote button.
  const roleHasLogin = member.userId != null;
  const syncLocked = member.importSyncMode === 'Locked';
  let roleOptions: TeamMemberRole[] = [];
  if (syncLocked) {
    if (member.role === 'SMS MANAGER' || member.role === 'SMS USER') roleOptions = ['SMS MANAGER', 'SMS USER'];
    else if (member.role === 'SMS EMPLOYEE' && roleHasLogin) roleOptions = ['SMS EMPLOYEE', 'SMS USER', 'SMS MANAGER'];
  } else {
    if (member.role === 'SMS USER') roleOptions = ['SMS USER', 'SMS MANAGER'];
    else if (member.role === 'SMS EMPLOYEE' && roleHasLogin) roleOptions = ['SMS EMPLOYEE', 'SMS USER', 'SMS MANAGER'];
  }
  const roleEditable = roleOptions.length > 0;

  // Promote — creates a brand-new login for an SMS Employee that has none.
  // The re-enable-an-existing-disabled-login case is handled by the Employee
  // Role field (subtask 4), not here.
  const canPromote = member.role === 'SMS EMPLOYEE' && member.userId == null;

  // Fresh-creation vs. reactivation is inferred client-side from whether the
  // member already had a linked user *before* this call — never from the
  // mutation's response, which is just the plain refreshed record.
  const promoteBody = member.user
    ? t('store_settings:promote_body_reactivate', { name: `${member.firstName} ${member.lastName}` })
    : t('store_settings:promote_body_new');

  const handlePromoteConfirm = async (roleChoice: 'SMS MANAGER' | 'SMS USER') => {
    const wasReactivate = !!member.user;
    setIsPromoteOpen(false);
    try {
      const next = await updateTeamMember.mutateAsync({ employeeId: member.id!, roleName: roleChoice });
      onMemberUpdated(next);
      if (wasReactivate) {
        setActionBanner({ tone: 'success', message: t('store_settings:promote_success_reactivate') });
      } else {
        setPromoteLoginInfo({ userName: next.user?.userName ?? '—', role: roleChoice });
      }
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:promote_error') });
    }
  };

  // Demote — Manager/User -> SMS Employee, disabling (not deleting) the
  // linked login. Withheld unless Importer Sync = Locked: while sync is on,
  // the importer would just revert the role on its next run.
  const canDemote = (member.role === 'SMS MANAGER' || member.role === 'SMS USER') && syncLocked;

  const handleDemoteConfirm = async () => {
    setIsDemoteOpen(false);
    try {
      const next = await updateTeamMember.mutateAsync({ employeeId: member.id!, roleName: 'SMS EMPLOYEE' });
      onMemberUpdated(next);
      setActionBanner({ tone: 'success', message: t('store_settings:demote_success') });
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:demote_error') });
    }
  };

  // canBecomePrimary — Role = SMS MANAGER (not SMS USER, per spec), linked
  // user, Active, and not already primary. The server itself only enforces
  // Role ∈ SmsValidRoles (Manager or User) + Status = Active (409
  // otherwise) — this button is intentionally narrower.
  const canBecomePrimary = member.role === 'SMS MANAGER' && member.userId != null && member.status === 'Active' && !member.user?.isPrimary;

  const handleSetPrimary = async () => {
    setIsSetPrimaryOpen(false);
    try {
      const next = await setPrimaryManager.mutateAsync({ employeeId: member.id! });
      onMemberUpdated(next);
      syncPrimaryContact(next);
      setActionBanner({ tone: 'success', message: t('store_settings:toast_set_primary') });
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:set_primary_error') });
    }
  };

  const isFixingRole = resyncUserRole.isLoading;
  const isFixingStatus = resyncUserStatus.isLoading;

  const handleFixRole = async () => {
    try {
      const next = await resyncUserRole.mutateAsync({ employeeId: member.id! });
      onMemberUpdated(next);
      setActionBanner({ tone: 'success', message: t('store_settings:toast_role_resynced') });
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:resync_role_error') });
    }
  };

  const handleFixStatus = async () => {
    try {
      const next = await resyncUserStatus.mutateAsync({ employeeId: member.id! });
      onMemberUpdated(next);
      setActionBanner({ tone: 'success', message: t('store_settings:toast_status_resynced') });
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:resync_status_error') });
    }
  };

  const handleCopyLoginInfo = async () => {
    if (!promoteLoginInfo) return;
    const text = [
      `${t('store_settings:promote_login_info_customer_label')}: ${chainName}`,
      `${t('store_settings:promote_login_info_user_label')}: ${promoteLoginInfo.userName}`,
      `${t('store_settings:promote_login_info_password_label')}: ${storeCode ?? ''}`,
      `${t('store_settings:promote_login_info_role_label')}: ${columnContext.roleLabels[promoteLoginInfo.role]}`,
    ].join('\n');
    await Clipboard.setStringAsync(text);
    setLoginInfoCopied(true);
    setTimeout(() => setLoginInfoCopied(false), 1400);
  };

  const handleSyncDialogSave = async (mode: ImportSyncMode, employeeNum?: string) => {
    const modeChanged = mode !== member.importSyncMode;
    setEditingField(null);
    try {
      const next = await updateTeamMember.mutateAsync({
        employeeId: member.id!,
        // The write DTO's importSyncMode is a real generated enum (unlike the
        // GET DTO's plain-string role/status/importSyncMode — see types.ts),
        // but its members are string-identical to our ImportSyncMode union,
        // so this cast is a same-value reinterpretation, not a real coercion.
        importSyncMode: mode as unknown as ImportSyncModeEnum,
        // Spread whenever defined (including '') — the server treats an
        // explicit empty string as "clear," and omitting the key as "leave
        // unchanged." Never drop the key just because it's empty.
        ...(employeeNum !== undefined ? { employeeNum } : {}),
      });
      onMemberUpdated(next);
      setActionBanner({
        tone: 'success',
        message: modeChanged ? t('store_settings:toast_sync_mode_updated') : t('store_settings:toast_employee_id_saved'),
      });
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:sync_save_error_banner') });
    }
  };

  const handleStatusDialogSave = async (status: TeamMemberStatus) => {
    setEditingField(null);
    try {
      // Same value-identical reinterpretation as importSyncMode above — the
      // write DTO's status is a real generated enum, string-identical to our
      // TeamMemberStatus union (see types.ts).
      const next = await updateTeamMember.mutateAsync({
        employeeId: member.id!,
        status: status as unknown as EmployeeStatusEnum,
      });
      onMemberUpdated(next);
      setActionBanner({ tone: 'success', message: t('store_settings:toast_status_updated') });
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:employee_status_save_error_banner') });
    }
  };

  const handleRoleDialogSave = async (role: TeamMemberRole) => {
    setEditingField(null);
    try {
      const next = await updateTeamMember.mutateAsync({ employeeId: member.id!, roleName: role });
      onMemberUpdated(next);
      setActionBanner({ tone: 'success', message: t('store_settings:toast_role_updated') });
    } catch {
      setActionBanner({ tone: 'error', message: t('store_settings:employee_role_save_error_banner') });
    }
  };

  return (
    <Modal isOpen={!!member} onClose={onClose} size="full">
      <Modal.Content maxW="1400px" w="95%" maxH="92%">
        <Modal.CloseButton />
        <Modal.Header>
          <VStack space={2}>
            <HStack justifyContent="space-between" alignItems="center" pr={8}>
              <HStack space={2} alignItems="center">
                <Text size="lg" fontWeight={700}>
                  {`${member.firstName ?? ''} ${member.lastName ?? ''}`.trim()}
                </Text>
                {member.user?.isPrimary ? (
                  <HStack space={1.5} alignItems="center">
                    <StarIcon size={4} color="amber.500" />
                    <Text size="sm" fontWeight={600} color="amber.500">
                      {columnContext.primaryLabel}
                    </Text>
                  </HStack>
                ) : (
                  canSetPrimary &&
                  canBecomePrimary && (
                    <Button
                      alternate
                      outline
                      bg="white"
                      size="sm"
                      onPress={() => setIsSetPrimaryOpen(true)}
                      leftIcon={<StarIcon size={4} color="amber.500" />}
                    >
                      {t('store_settings:set_as_primary_manager')}
                    </Button>
                  )
                )}
              </HStack>
              <Button alternate outline size="sm" onPress={() => setIsPrintOpen(true)} leftIcon={<PrinterIcon size={4} />}>
                {t('store_settings:print_button_label')}
              </Button>
            </HStack>
            <HStack space={2} alignItems="center">
              <Badge
                {...statusBadgeStyle(member.status)}
                label={columnContext.statusLabels[member.status]}
                LeadingIcon={member.status === 'Active' ? UserRoundCheckIcon : UserRoundXIcon}
                size="sm"
              />
              <Badge {...roleBadgeStyle(member.role)} label={columnContext.roleLabels[member.role]} size="sm" />
            </HStack>
          </VStack>
        </Modal.Header>
        {!!actionBanner && (
          <Box mx={6} mt={3} bg={actionBanner.tone === 'success' ? 'success.50' : 'error.50'} rounded="md" px={4} py={2.5}>
            <Text size="sm" fontWeight={500} color={actionBanner.tone === 'success' ? 'success.700' : 'error.700'}>
              {actionBanner.message}
            </Text>
          </Box>
        )}
        <Modal.Body>
          <ScrollView ref={scrollRef}>
            <VStack space={3}>
              <SectionCard
                variant="plain"
                title={t('store_settings:section_boh_integration')}
                icon={<RefreshCwIcon size={4} color="gray.700" />}
              >
                <VStack space={4}>
                  <HStack space={6} flexWrap="wrap">
                    <Box flex={1} minW="170px">
                      <ReadOnlyField
                        label={t('store_settings:import_source')}
                        value={
                          member.importSource === 'Manual'
                            ? t('store_settings:import_source_manual')
                            : t('store_settings:import_source_importer')
                        }
                        muted={member.importSource === 'Importer'}
                      />
                    </Box>
                    <Box flex={1} minW="170px">
                      <ReadOnlyField label={t('store_settings:back_office_vendor')} value={vendorName} />
                    </Box>
                    <Box flex={1} minW="170px">
                      <ReadOnlyField label={t('store_settings:employee_id')} value={member.employeeNum} />
                    </Box>
                    <Box flex={1} minW="170px" />
                  </HStack>
                  <HStack space={6} flexWrap="wrap">
                    <BadgeField label={columnContext.autoSyncPrefixLabel}>
                      <HStack space={1} alignItems="center">
                        <Badge
                          {...syncModeBadgeStyle(member.importSyncMode)}
                          label={
                            member.importSyncMode === 'Locked'
                              ? columnContext.autoSyncOffLabel
                              : member.importSyncMode === 'Protected'
                              ? columnContext.autoSyncProtectedLabel
                              : columnContext.autoSyncOnLabel
                          }
                          LeadingIcon={member.importSyncMode !== 'Auto' ? RefreshCwIcon : undefined}
                          size="sm"
                        />
                        {!bohEditLock && <FieldEditPencil onPress={() => setEditingField('importerSync')} />}
                      </HStack>
                    </BadgeField>
                    <BadgeField label={t('store_settings:employment_status')}>
                      <HStack space={1} alignItems="center">
                        <Badge
                          {...statusBadgeStyle(member.status)}
                          label={columnContext.statusLabels[member.status]}
                          LeadingIcon={member.status === 'Active' ? UserRoundCheckIcon : UserRoundXIcon}
                          size="sm"
                        />
                        {syncLocked && !bohEditLock && <FieldEditPencil onPress={() => setEditingField('employeeStatus')} />}
                      </HStack>
                    </BadgeField>
                    <BadgeField label={t('store_settings:role')}>
                      <HStack space={1} alignItems="center">
                        <Badge {...roleBadgeStyle(member.role)} label={columnContext.roleLabels[member.role]} size="sm" />
                        {roleEditable && !bohEditLock && <FieldEditPencil onPress={() => setEditingField('employeeRole')} />}
                      </HStack>
                    </BadgeField>
                    <Box flex={1} minW="170px" />
                  </HStack>
                </VStack>
              </SectionCard>

              <SectionCard
                variant="form"
                title={t('store_settings:section_employee_record')}
                icon={<UserRoundIcon size={4} color="gray.700" />}
                isEditing={isEditingEmployee.value}
                onEdit={member.importSyncMode !== 'Auto' ? isEditingEmployee.setTrue : undefined}
                _formActionBar={{
                  countDirtyFields: employeeCountDirty,
                  isDisabled: !employeeForm.formState.isDirty || !employeeForm.formState.isValid || isEmployeeSaving,
                  isLoading: isEmployeeSaving,
                  onCancel: handleEmployeeRecordCancel,
                  onSave: handleEmployeeRecordSave,
                }}
              >
                {member.importSyncMode === 'Auto' && (
                  <HStack
                    bg="blue.50"
                    borderLeftWidth={3}
                    borderColor="blue.400"
                    px={3}
                    py={2}
                    mb={4}
                    space={2}
                    alignItems="center"
                    rounded="sm"
                  >
                    <RefreshCwIcon size={4} color="blue.500" />
                    <Text size="sm" fontWeight={400} color="blue.800" flex={1}>
                      {t('store_settings:employee_record_sync_locked_banner')}
                    </Text>
                    <Button
                      alternate
                      outline
                      bg="white"
                      size="sm"
                      onPress={handleOpenImporterSyncFromBanner}
                      leftIcon={<PencilIcon size={4} color="blue.700" />}
                      _text={{ color: 'blue.700' }}
                    >
                      {t('store_settings:employee_record_sync_locked_action')}
                    </Button>
                  </HStack>
                )}
                <VStack space={4}>
                  <HStack space={6} flexWrap="wrap">
                    <Box flex={1} minW="170px">
                      {isEditingEmployee.value ? (
                        <ControlInput
                          control={employeeForm.control}
                          name="firstName"
                          label={`${t('store_settings:first_name')} *`}
                          disabled={isEmployeeSaving}
                          renderErrorText={renderErrorText}
                        />
                      ) : (
                        <ReadOnlyField label={t('store_settings:first_name')} value={member.firstName} />
                      )}
                    </Box>
                    <Box flex={1} minW="170px">
                      {isEditingEmployee.value ? (
                        <ControlInput
                          control={employeeForm.control}
                          name="lastName"
                          label={`${t('store_settings:last_name')} *`}
                          disabled={isEmployeeSaving}
                          renderErrorText={renderErrorText}
                        />
                      ) : (
                        <ReadOnlyField label={t('store_settings:last_name')} value={member.lastName} />
                      )}
                    </Box>
                    <Box flex={1} minW="170px">
                      {isEditingEmployee.value ? (
                        <ControlInput
                          control={employeeForm.control}
                          name="title"
                          label={t('store_settings:job_title')}
                          disabled={isEmployeeSaving}
                          renderErrorText={renderErrorText}
                        />
                      ) : (
                        <ReadOnlyField label={t('store_settings:job_title')} value={member.title} />
                      )}
                    </Box>
                    <Box flex={1} minW="170px" />
                  </HStack>
                  <HStack space={6} flexWrap="wrap">
                    <Box flex={1} minW="170px">
                      {isEditingEmployee.value ? (
                        <Controller
                          control={employeeForm.control}
                          name="birthDate"
                          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <Box>
                              <Text size="md" fontWeight={500} color="gray.700" mb={0.5}>
                                {t('store_settings:birthday')}
                              </Text>
                              <InputField
                                value={value}
                                onBlur={onBlur}
                                onChangeText={next => onChange(maskMonthDay(next))}
                                placeholder={t('store_settings:birthday_placeholder')}
                                keyboardType="numeric"
                                isInvalid={!!error?.message}
                                isDisabled={isEmployeeSaving}
                              />
                              {!!error?.message && (
                                <Box pt={0.5}>
                                  <FormHelperText error visible>
                                    {renderErrorText(error.message)}
                                  </FormHelperText>
                                </Box>
                              )}
                            </Box>
                          )}
                        />
                      ) : (
                        <ReadOnlyField label={t('store_settings:birthday')} value={formatBirthday(member.birthDate)} />
                      )}
                    </Box>
                    <Box flex={1} minW="170px">
                      {isEditingEmployee.value ? (
                        <Controller
                          control={employeeForm.control}
                          name="hireDate"
                          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <Box>
                              <Text size="md" fontWeight={500} color="gray.700" mb={0.5}>
                                {t('store_settings:hire_date')}
                              </Text>
                              <InputField
                                value={value}
                                onBlur={onBlur}
                                onChangeText={next => onChange(maskDate(next))}
                                placeholder={t('store_settings:hire_date_placeholder')}
                                keyboardType="numeric"
                                isInvalid={!!error?.message}
                                isDisabled={isEmployeeSaving}
                              />
                              {!!error?.message && (
                                <Box pt={0.5}>
                                  <FormHelperText error visible>
                                    {renderErrorText(error.message)}
                                  </FormHelperText>
                                </Box>
                              )}
                            </Box>
                          )}
                        />
                      ) : (
                        <ReadOnlyField label={t('store_settings:hire_date')} value={formatDateShort(member.hireDate)} />
                      )}
                    </Box>
                    <Box flex={1} minW="170px">
                      {isEditingEmployee.value ? (
                        <ControlInput
                          control={employeeForm.control}
                          name="email"
                          label={t('store_settings:email')}
                          disabled={isEmployeeSaving}
                          renderErrorText={renderErrorText}
                        />
                      ) : (
                        <ReadOnlyField label={t('store_settings:email')} value={member.email} />
                      )}
                    </Box>
                    <Box flex={1} minW="170px">
                      {isEditingEmployee.value ? (
                        <ControlInput
                          control={employeeForm.control}
                          name="mobileNumber"
                          label={t('store_settings:mobile_phone')}
                          disabled={isEmployeeSaving}
                          renderErrorText={renderErrorText}
                        />
                      ) : (
                        <ReadOnlyField label={t('store_settings:mobile_phone')} value={member.mobileNumber} />
                      )}
                    </Box>
                  </HStack>
                </VStack>
              </SectionCard>

              <SectionCard
                variant="plain"
                title={t('store_settings:section_user_information')}
                icon={<ShieldUserIcon size={4} color="gray.700" />}
                headerRight={
                  (canPromote || canDemote) && (
                    <HStack space={2}>
                      {canPromote && (
                        <Button size="sm" onPress={() => setIsPromoteOpen(true)} leftIcon={<CircleArrowUpIcon size={4} color="black" />}>
                          {t('store_settings:promote_button_label')}
                        </Button>
                      )}
                      {canDemote && (
                        <Button
                          alternate
                          outline
                          bg="white"
                          size="sm"
                          onPress={() => setIsDemoteOpen(true)}
                          leftIcon={<CircleArrowDownIcon size={4} color="gray.600" />}
                        >
                          {t('store_settings:demote_button_label')}
                        </Button>
                      )}
                    </HStack>
                  )
                }
              >
                {member.user ? (
                  <VStack space={4}>
                    {hasStatusMismatch(member) && (
                      <HStack
                        bg="amber.50"
                        borderLeftWidth={3}
                        borderColor="amber.500"
                        px={3}
                        py={2}
                        space={2}
                        alignItems="center"
                        rounded="sm"
                      >
                        <WarningIcon size={4} color="amber.600" />
                        <Text size="sm" fontWeight={400} color="amber.800" flex={1}>
                          {t('store_settings:status_mismatch_banner', {
                            employeeStatus: columnContext.statusLabels[member.status],
                            userStatus: columnContext.userStatusLabels[member.user.status],
                          })}
                        </Text>
                        {canEditRoster && (
                          <Button
                            alternate
                            outline
                            bg="white"
                            size="sm"
                            onPress={handleFixStatus}
                            isLoading={isFixingStatus}
                            disabled={isFixingStatus}
                            leftIcon={<RefreshCwIcon size={4} color="amber.700" />}
                          >
                            {t('store_settings:fix_label')}
                          </Button>
                        )}
                      </HStack>
                    )}
                    {hasRoleMismatch(member) && (
                      <HStack
                        bg="amber.50"
                        borderLeftWidth={3}
                        borderColor="amber.500"
                        px={3}
                        py={2}
                        space={2}
                        alignItems="center"
                        rounded="sm"
                      >
                        <WarningIcon size={4} color="amber.600" />
                        <Text size="sm" fontWeight={400} color="amber.800" flex={1}>
                          {t('store_settings:role_mismatch_banner', {
                            employeeRole: columnContext.roleLabels[member.role],
                            userRole: columnContext.roleLabels[member.user.userRole],
                          })}
                        </Text>
                        {canEditRoster && (
                          <Button
                            alternate
                            outline
                            bg="white"
                            size="sm"
                            onPress={handleFixRole}
                            isLoading={isFixingRole}
                            disabled={isFixingRole}
                            leftIcon={<RefreshCwIcon size={4} color="amber.700" />}
                          >
                            {t('store_settings:fix_label')}
                          </Button>
                        )}
                      </HStack>
                    )}
                    <HStack space={6} flexWrap="wrap">
                      <Box flex={1} minW="170px">
                        <ReadOnlyField label={t('store_settings:username')} value={member.user.userName} />
                      </Box>
                      <Box flex={1} minW="170px">
                        <ReadOnlyField label={t('store_settings:user_contact_email')} value={member.user.email} />
                      </Box>
                      <Box flex={1} minW="170px">
                        <ReadOnlyField
                          label={t('store_settings:recovery_email')}
                          value={
                            member.user.recoveryEmail && member.user.recoveryEmailStatus === RECOVERY_EMAIL_STATUS_VERIFIED
                              ? member.user.recoveryEmail
                              : undefined
                          }
                        />
                      </Box>
                      <BadgeField label={t('store_settings:pin_set')}>
                        <YesNoBadge
                          value={!!member.user.pinSet}
                          yesLabel={columnContext.yesLabel}
                          noLabel={columnContext.noLabel}
                          size="sm"
                        />
                      </BadgeField>
                    </HStack>
                    <HStack space={6} flexWrap="wrap">
                      <BadgeField label={t('store_settings:user_status')}>
                        <Badge
                          {...userStatusBadgeStyle(member.user.status)}
                          label={columnContext.userStatusLabels[member.user.status]}
                          LeadingIcon={ShieldUserIcon}
                          size="sm"
                        />
                      </BadgeField>
                      <BadgeField label={t('store_settings:user_role')}>
                        <Badge {...roleBadgeStyle(member.user.userRole)} label={columnContext.roleLabels[member.user.userRole]} size="sm" />
                      </BadgeField>
                      <Box flex={1} minW="170px" />
                      <Box flex={1} minW="170px" />
                    </HStack>
                  </VStack>
                ) : (
                  <Text size="sm" color="gray.700">
                    {t('store_settings:no_login_callout')}
                  </Text>
                )}
              </SectionCard>

              <SectionCard variant="plain" title={t('store_settings:section_history')} icon={<ClockIcon size={4} color="gray.700" />}>
                <HStack space={6} flexWrap="wrap">
                  <Box flex={1} minW="170px">
                    <ReadOnlyField
                      label={t('store_settings:last_login')}
                      value={
                        member.user ? formatRelativeDays(member.user.lastLoginDateUtc, t, columnContext.utcOffsetInMinutes) : undefined
                      }
                    />
                  </Box>
                  <Box flex={1} minW="170px">
                    <ReadOnlyField
                      label={t('store_settings:last_scheduled')}
                      value={formatRelativeDays(member.lastScheduledDateUtc, t, columnContext.utcOffsetInMinutes)}
                    />
                  </Box>
                  <Box flex={1} minW="170px" />
                  <Box flex={1} minW="170px" />
                </HStack>
              </SectionCard>
            </VStack>
          </ScrollView>
        </Modal.Body>
      </Modal.Content>
      <MemberProfilePrintView
        isOpen={isPrintOpen}
        onClose={() => setIsPrintOpen(false)}
        member={member}
        columnContext={columnContext}
        siteName={siteName}
        customerName={customerName}
        address={address}
        chainName={chainName}
        vendorName={vendorName}
        brandLogoUrl={brandLogoUrl}
        generatedByName={generatedByName}
      />
      <FieldSelectDialog
        isOpen={editingField === 'importerSync'}
        onClose={() => setEditingField(null)}
        title={columnContext.autoSyncPrefixLabel}
        currentValue={member.importSyncMode}
        cancelLabel={t('store_settings:cancel')}
        saveLabel={t('store_settings:save')}
        onSave={(v, id) => handleSyncDialogSave(v as ImportSyncMode, id)}
        options={(['Auto', 'Protected', 'Locked'] as ImportSyncMode[]).map(mode => ({
          value: mode,
          label:
            mode === 'Locked'
              ? columnContext.autoSyncOffLabel
              : mode === 'Protected'
              ? columnContext.autoSyncProtectedLabel
              : columnContext.autoSyncOnLabel,
          description: t(`store_settings:sync_desc_${mode === 'Locked' ? 'off' : mode === 'Protected' ? 'protected' : 'on'}`),
          tone: mode === 'Auto' ? 'accent' : mode === 'Protected' ? 'warning' : 'danger',
          leading: <RefreshCwIcon size={5} color={importSyncIconColor(mode)} />,
        }))}
        input={{
          label: t('store_settings:employee_id'),
          initialValue: member.employeeNum ?? '',
          caution: t('store_settings:sync_employee_id_caution'),
          required: sel => sel !== 'Locked',
          resetLabel: t('store_settings:sync_employee_id_reset_label'),
        }}
      />
      <FieldSelectDialog
        isOpen={editingField === 'employeeStatus'}
        onClose={() => setEditingField(null)}
        title={t('store_settings:employment_status')}
        currentValue={member.status}
        cancelLabel={t('store_settings:cancel')}
        saveLabel={t('store_settings:save')}
        onSave={v => handleStatusDialogSave(v as TeamMemberStatus)}
        options={(['Active', 'Inactive', 'Terminated'] as TeamMemberStatus[]).map(status => ({
          value: status,
          label: columnContext.statusLabels[status],
          description: t(
            `store_settings:key_help_employee_${
              status === 'Active' ? 'active' : status === 'Inactive' ? 'inactive' : 'terminated'
            }_description`,
          ),
          tone: status === 'Active' ? 'success' : status === 'Inactive' ? 'neutral' : 'danger',
          leading:
            status === 'Active' ? (
              <UserRoundCheckIcon size={5} color="success.600" />
            ) : (
              <UserRoundXIcon size={5} color={status === 'Inactive' ? 'gray.400' : 'error.500'} />
            ),
        }))}
      />
      <FieldSelectDialog
        isOpen={editingField === 'employeeRole'}
        onClose={() => setEditingField(null)}
        title={t('store_settings:role')}
        subtitle={!syncLocked ? t('store_settings:role_promote_only_note') : undefined}
        currentValue={member.role}
        cancelLabel={t('store_settings:cancel')}
        saveLabel={t('store_settings:save')}
        onSave={v => handleRoleDialogSave(v as TeamMemberRole)}
        options={roleOptions.map(role => ({
          value: role,
          label: columnContext.roleLabels[role],
          description: t(`store_settings:role_desc_${role === 'SMS MANAGER' ? 'manager' : role === 'SMS USER' ? 'user' : 'employee'}`),
          tone: role === 'SMS MANAGER' ? 'warning' : role === 'SMS USER' ? 'accent' : 'neutral',
          leading: <Box w={3} h={3} rounded="full" bg={roleBadgeStyle(role).bg} />,
        }))}
      />
      <FieldSelectDialog
        isOpen={isPromoteOpen}
        onClose={() => setIsPromoteOpen(false)}
        title={t('store_settings:promote_title')}
        titleIcon={<ShieldUserIcon size={5} color="gray.700" />}
        subtitle={promoteBody}
        subtitleVariant="body"
        currentValue="SMS USER"
        showCurrentBadge={false}
        requireChange={false}
        cancelLabel={t('store_settings:cancel')}
        saveLabel={t('store_settings:promote_confirm')}
        onSave={value => handlePromoteConfirm(value as 'SMS MANAGER' | 'SMS USER')}
        options={[
          {
            value: 'SMS MANAGER',
            label: columnContext.roleLabels['SMS MANAGER'],
            description: t('store_settings:role_desc_manager'),
            tone: 'warning',
            leading: <Box w={3} h={3} rounded="full" bg={roleBadgeStyle('SMS MANAGER').bg} />,
          },
          {
            value: 'SMS USER',
            label: columnContext.roleLabels['SMS USER'],
            description: t('store_settings:role_desc_user'),
            tone: 'accent',
            leading: <Box w={3} h={3} rounded="full" bg={roleBadgeStyle('SMS USER').bg} />,
          },
        ]}
      />
      <ConfirmModal
        isOpen={!!promoteLoginInfo}
        onClose={() => setPromoteLoginInfo(null)}
        onConfirm={() => setPromoteLoginInfo(null)}
        title={t('store_settings:promote_login_info_title')}
        titleIcon={<ShieldUserIcon size={5} color="success.700" />}
        body={t('store_settings:promote_login_info_intro')}
        hideCancel
        confirmLabel={t('store_settings:promote_login_info_done_label')}
        footerLeft={
          <Button
            alternate
            outline
            size="sm"
            onPress={handleCopyLoginInfo}
            leftIcon={loginInfoCopied ? <CheckCircle size={4} color="success.600" /> : <CopyIcon size={4} color="gray.600" />}
          >
            {loginInfoCopied ? t('store_settings:promote_login_info_copied_label') : t('store_settings:promote_login_info_copy_label')}
          </Button>
        }
      >
        <VStack space={3}>
          <HStack space={3}>
            <InfoCard label={t('store_settings:promote_login_info_customer_label')}>{chainName}</InfoCard>
            <InfoCard label={t('store_settings:promote_login_info_user_label')}>{promoteLoginInfo?.userName}</InfoCard>
          </HStack>
          <HStack space={3}>
            <InfoCard label={t('store_settings:promote_login_info_password_label')}>{storeCode}</InfoCard>
            <InfoCard label={t('store_settings:promote_login_info_role_label')}>
              {promoteLoginInfo ? columnContext.roleLabels[promoteLoginInfo.role] : ''}
            </InfoCard>
          </HStack>
        </VStack>
      </ConfirmModal>
      <ConfirmModal
        isOpen={isDemoteOpen}
        onClose={() => setIsDemoteOpen(false)}
        onConfirm={handleDemoteConfirm}
        title={t('store_settings:demote_title')}
        titleIcon={<ShieldUserIcon size={5} color="error.600" />}
        body={t('store_settings:demote_body')}
        cancelLabel={t('store_settings:cancel')}
        confirmLabel={t('store_settings:demote_confirm')}
        tone="danger"
      />
      <ConfirmModal
        isOpen={isSetPrimaryOpen}
        onClose={() => setIsSetPrimaryOpen(false)}
        onConfirm={handleSetPrimary}
        title={t('store_settings:set_primary_title')}
        titleIcon={<StarIcon size={5} color="amber.500" />}
        body={t('store_settings:set_primary_body', { name: `${member.firstName} ${member.lastName}` })}
        cancelLabel={t('store_settings:cancel')}
        confirmLabel={t('store_settings:set_as_primary_manager')}
      >
        <Box bg="amber.50" borderWidth={1} borderColor="amber.200" rounded="md" p={3}>
          <Text size="sm" color="amber.800">
            {t('store_settings:set_primary_hint')}
          </Text>
        </Box>
      </ConfirmModal>
    </Modal>
  );
};

export default MemberProfileModal;
